import React, { useState } from 'react';
import { Tenant, User } from '../types/user';
import { userManagementService } from '../services/user-management-service';
import { X, Mail, User as UserIcon, Shield, Link as LinkIcon, UserPlus, Copy, Check, Lock } from 'lucide-react';

interface InviteUserModalProps {
  tenant: Tenant;
  currentUser: User;
  onClose: () => void;
  onSuccess: () => void;
}

type Mode = 'choose' | 'direct' | 'link';

export function InviteUserModal({ tenant, currentUser, onClose, onSuccess }: InviteUserModalProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'manager'
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleDirectCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Nome e email são obrigatórios');
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      setError('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const result = await userManagementService.createUserDirectly(
        tenant.id,
        formData.email,
        formData.name,
        formData.password,
        formData.role
      );

      if (!result.success) {
        setError(result.error || 'Erro ao criar usuário');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      console.error('Erro ao criar usuário:', err);
      setError('Erro ao criar usuário. Tente novamente.');
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await userManagementService.getTenantInviteLink(tenant.id);

      if (!result.success || !result.link) {
        setError(result.error || 'Erro ao gerar link');
        setLoading(false);
        return;
      }

      setGeneratedLink(result.link);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao gerar link:', err);
      setError('Erro ao gerar link. Tente novamente.');
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Adicionar Novo Usuário</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Choose Mode */}
        {mode === 'choose' && (
          <div className="p-6 space-y-4">
            <p className="text-gray-600 mb-4">
              Escolha como deseja adicionar o usuário à sua empresa.
            </p>

            {/* Opção 1: Criar Direto */}
            <button
              onClick={() => setMode('direct')}
              className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors flex-shrink-0">
                  <UserPlus className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1.5">Criar Direto</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Você cria a conta completa (nome, email e senha) e o usuário já pode fazer login
                  </p>
                </div>
              </div>
            </button>

            {/* Opção 2: Gerar Link */}
            <button
              onClick={() => setMode('link')}
              className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors flex-shrink-0">
                  <LinkIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1.5">Link de Convite</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Link permanente da empresa. Qualquer pessoa pode se cadastrar e será vinculada automaticamente
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Direct Create Form */}
        {mode === 'direct' && !success && (
          <form onSubmit={handleDirectCreate} className="p-6 space-y-5">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="João Silva"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="joao@empresa.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  disabled={loading}
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">O usuário poderá alterar depois</p>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Função
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'manager' })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  disabled={loading}
                >
                  <option value="user">Usuário</option>
                  <option value="manager">Administrador</option>
                </select>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setMode('choose')}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 font-medium shadow-sm"
                disabled={loading}
              >
                {loading ? 'Criando...' : 'Criar Usuário'}
              </button>
            </div>
          </form>
        )}

        {/* Link Generation */}
        {mode === 'link' && (
          <div className="p-6 space-y-5">
            {!generatedLink ? (
              <>
                {/* Info */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <LinkIcon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-purple-900 mb-1">
                        Link Permanente da Empresa
                      </p>
                      <p className="text-xs text-purple-700">
                        Este é um link único e permanente da sua empresa. Qualquer pessoa que se cadastrar usando este link será automaticamente vinculada como usuário.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setMode('choose')}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleGenerateLink}
                    className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 font-medium shadow-sm"
                    disabled={loading}
                  >
                    {loading ? 'Gerando...' : 'Gerar Link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Link Generated */}
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LinkIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Link da Empresa!</h3>
                  <p className="text-sm text-gray-500">
                    Compartilhe este link para que pessoas se cadastrem na sua empresa
                  </p>
                </div>

                {/* Link Display */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-2">Link de Convite:</p>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                      {generatedLink}
                    </code>
                    <button
                      onClick={handleCopyLink}
                      className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span className="text-xs">{copied ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-800">
                      <strong>Link Permanente:</strong> Este link não expira e pode ser usado quantas vezes quiser. Todas as pessoas que se cadastrarem serão automaticamente vinculadas à sua empresa.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedLink('');
                      setMode('choose');
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Gerar Outro Link
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-sm"
                  >
                    Concluir
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Usuário Criado!</h3>
              <p className="text-sm text-gray-500">
                O usuário já pode fazer login com o email e senha cadastrados.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
